/**
 * The ONE place MCP-only plan labels and limits are defined (ORP-21466).
 *
 * Why one file: the seat numbers in the 2026-08-23 design are a DRAFT. Spreading them across the
 * plan chip, the billing screen and the compare table would mean three edits and three chances to
 * miss one, so every MCP surface reads from here.
 *
 * ⚠ SCOPE — the price matrix (Read 20/30/55/120, Action 150/200/300/600, per-connection overage,
 * the annual discount) and the per-tier SEAT limit are deliberately NOT here yet: they land with
 * FE-15's plans/billing screens, which own the checkout wire too. What this file owns today is the
 * tier vocabulary plus the three VERIFIED backend read points for the live counters, expressed as
 * pure readers so the wiring wave has nothing to re-derive.
 *
 * ⚠ The daily tool-call quota is NOT modelled and must not be: ratified 2026-08-23, no MCP screen
 * shows a call counter.
 */

/** The MCP product ladder. Web packages are NOT in this union — they are a different product. */
export type McpPlanTier = 'free' | 'read' | 'action'

export const MCP_PLAN_TIERS: readonly McpPlanTier[] = ['free', 'read', 'action'] as const

/**
 * Tier → i18n key for the plan's display name. Keys, never copy: the caller resolves them.
 *
 * They point into the `shell` bundle rather than this feature's, because the plan chip renders on
 * EVERY MCP screen while a feature bundle only merges on its own route. Reserved bundles are eager
 * (`shared/i18n/loader.ts`), so the tier name is there from the first paint on all four screens —
 * and FE-15's billing surfaces can resolve the same keys without a second copy of the vocabulary.
 */
export const MCP_PLAN_LABEL_KEYS: Readonly<Record<McpPlanTier, string>> = {
  free: 'shell.mcp.plan.tier.free',
  read: 'shell.mcp.plan.tier.read',
  action: 'shell.mcp.plan.tier.action',
}

/**
 * A counter with a ceiling. `limit: null` is the wire's "no ceiling" (the backend sends it for
 * uncapped accounts) and is NOT the same as `undefined`, which means "not known yet" — the plan
 * chip hides an unknown limit rather than claiming the account is unlimited.
 */
export interface McpPlanCounter {
  used: number | null
  limit: number | null
}

/** What every MCP plan surface renders from. */
export interface McpPlanSummary {
  /** `null` until the account's MCP package is readable (FE-15 wires the tier). */
  tier: McpPlanTier | null
  /** Ad-account connections — the price meter (one client ad account = one connection). */
  connections: McpPlanCounter | null
  /** Seats — people who may reach the account, whether or not they connected an assistant. */
  users: McpPlanCounter | null
}

/** `GET /integrations/{workspaceId}` → `paid_media_connections` (verified backend surface). */
export interface PaidMediaConnectionsDto {
  paid_media_connections?: { used?: number | null; limit?: number | null } | null
}

/** Bootstrap `account_detail` → `connected_user_count` (verified backend surface). */
export interface ConnectedUserCountSource {
  connected_user_count?: number | null
}

/** MCP connections list → `account_user_counts` (verified backend surface). */
export interface AccountUserCountsSource {
  account_user_counts?: Record<string, number> | null
}

const finite = (value: unknown): number | null =>
  typeof value === 'number' && Number.isFinite(value) ? value : null

/**
 * READ POINT 1 — the connection counter, straight off `GET /integrations/{ws}`.
 *
 * Returns `null` when the payload carries no `paid_media_connections` block at all: an absent
 * block is not a zero, and rendering "0 of 0 connections" for a working account would be a lie.
 */
export function readConnectionCounter(
  source: PaidMediaConnectionsDto | null | undefined,
): McpPlanCounter | null {
  const block = source?.paid_media_connections
  if (!block) return null
  return { used: finite(block.used), limit: finite(block.limit) }
}

/**
 * READ POINT 2 — seats USED, from bootstrap's `account_detail.connected_user_count`.
 *
 * The seat LIMIT has no backend field: it is a property of the plan, so it is passed in by the
 * caller once FE-15 owns the tier→seats table. Until then it is `null` and the chip renders the
 * connections half only.
 */
export function readSeatCounter(
  source: ConnectedUserCountSource | null | undefined,
  limit: number | null = null,
): McpPlanCounter | null {
  const used = finite(source?.connected_user_count)
  if (used === null && limit === null) return null
  return { used, limit }
}

/**
 * READ POINT 3 — distinct MCP users, from the MCP connections list's `account_user_counts`.
 *
 * NOT the same number as seats: one person who connected Claude AND ChatGPT is two connections but
 * one MCP user, and a seat holder who never connected an assistant is a seat with no MCP user.
 * The Assistants screen (ORP-21469) is what renders it.
 *
 * `accountId` narrows the read to ONE account, which is what a screen bound to the selected account
 * needs: the listing unions every account the caller owns, so the sum across the map would show an
 * agency owner the total of all their accounts beside a limit that only ever bound one of them. An
 * account with no entry reads `null` ("not known"), never `0` — the map only carries accounts the
 * current page actually returned rows for, so a missing key means unlisted, not empty.
 *
 * Called without it the map is summed, which is the whole-listing figure and stays the default.
 */
export function readMcpUserCount(
  source: AccountUserCountsSource | null | undefined,
  accountId?: number | string | null,
): number | null {
  const counts = source?.account_user_counts
  if (!counts) return null
  if (accountId !== undefined && accountId !== null) {
    return finite(counts[String(accountId)])
  }
  const total = Object.values(counts).reduce<number>((sum, value) => sum + (finite(value) ?? 0), 0)
  return total
}

/** Compose the three reads into the one shape every MCP surface renders. */
export function resolveMcpPlanSummary(input: Partial<McpPlanSummary> = {}): McpPlanSummary {
  return {
    tier: input.tier ?? null,
    connections: input.connections ?? null,
    users: input.users ?? null,
  }
}
